import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useWallet, txDisplayName, txTimeLabel } from '../../contexts/WalletContext';
import ScanQrIcon from '../icons/ScanQrIcon';
import { WalletQuickPlusIcon, WalletQuickReceiveIcon, WalletQuickSendIcon } from '../icons/WalletQuickActionIcons';

// Eye open icon
const EyeOpen = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

// Eye closed icon
const EyeOff = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const HomeScreen = ({ onNavigate, t, isRTL }) => {
  const [balanceVisible, setBalanceVisible] = useState(true);
  const { balance, transactions } = useWallet();

  // Show only the 5 most recent transactions on the home screen
  const recentTransactions = transactions.slice(0, 5);

  // Format balance: "18,224" + ".00 MAD"
  const balanceInt = Math.floor(balance).toLocaleString('en-US');
  const balanceDecimal = (balance - Math.floor(balance)).toFixed(2).slice(1); // ".00"

  const tr = t || {
    greet: 'Salam,', walletBalance: 'Wallet Balance', send: 'Send',
    receive: 'Receive', topUp: 'Top Up', scanQR: 'Scan QR', recent: 'Recent',
    seeAll: 'See all', hideBalance: 'Hide', showBalance: 'Show',
  };

  return (
    <div
      data-testid="home-screen"
      className="no-scrollbar"
      style={{
        background: '#0F0F0F',
        height: '100%',
        overflowY: 'auto',
        fontFamily: 'Inter, sans-serif',
        paddingBottom: '12px',
        direction: isRTL ? 'rtl' : 'ltr',
      }}
    >
      {/* Header */}
      <div style={{ padding: '18px 20px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <p style={{ color: '#8B93A7', fontSize: '12px', margin: 0 }}>{tr.greet}</p>
          <p style={{ color: '#F5F1EA', fontSize: '19px', fontWeight: '700', margin: 0, letterSpacing: '-0.4px' }}>Ahmed</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ position: 'relative' }}>
            <motion.button
              whileTap={{ scale: 0.9 }}
              style={{ width: '34px', height: '34px', borderRadius: '11px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#8B93A7" strokeWidth="1.8">
                <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 01-3.46 0" />
              </svg>
            </motion.button>
            <div style={{ position: 'absolute', top: '6px', right: '6px', width: '6px', height: '6px', borderRadius: '50%', background: '#D4AF37', border: '1.5px solid #0F0F0F' }} />
          </div>
          <motion.button
            data-testid="home-profile-btn"
            whileTap={{ scale: 0.9 }}
            onClick={() => onNavigate('profile')}
            style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'linear-gradient(135deg, #D4AF37, #B8962E)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px', fontWeight: '800', color: '#0F0F0F', border: 'none', cursor: 'pointer' }}
          >
            A
          </motion.button>
        </div>
      </div>

      {/* Balance Card */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        style={{ padding: '0 20px 18px' }}
      >
        <div
          style={{
            background: 'linear-gradient(135deg, rgba(212,175,55,0.12) 0%, rgba(212,175,55,0.05) 50%, rgba(255,255,255,0.03) 100%)',
            border: '1px solid rgba(212,175,55,0.22)',
            borderRadius: '24px',
            padding: '22px',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
          }}
        >
          {/* Moroccan Monument Background - Marrakesh Koutoubia Mosque Minaret */}
          <svg 
            style={{ position: 'absolute', right: '-10px', bottom: '-20px', opacity: 0.12, pointerEvents: 'none' }}
            width="160" 
            height="200" 
            viewBox="0 0 160 200" 
            fill="none"
          >
            {/* Main minaret body */}
            <rect x="45" y="100" width="70" height="100" fill="#D4AF37" opacity="0.3"/>
            <rect x="47" y="102" width="66" height="96" fill="none" stroke="#D4AF37" strokeWidth="2"/>
            
            {/* Decorative arched windows pattern */}
            <path d="M 55 115 Q 60 110 65 115" stroke="#D4AF37" strokeWidth="2.5" fill="none"/>
            <path d="M 70 115 Q 75 110 80 115" stroke="#D4AF37" strokeWidth="2.5" fill="none"/>
            <path d="M 85 115 Q 90 110 95 115" stroke="#D4AF37" strokeWidth="2.5" fill="none"/>
            
            <path d="M 55 135 Q 60 130 65 135" stroke="#D4AF37" strokeWidth="2.5" fill="none"/>
            <path d="M 70 135 Q 75 130 80 135" stroke="#D4AF37" strokeWidth="2.5" fill="none"/>
            <path d="M 85 135 Q 90 130 95 135" stroke="#D4AF37" strokeWidth="2.5" fill="none"/>
            
            <path d="M 55 155 Q 60 150 65 155" stroke="#D4AF37" strokeWidth="2.5" fill="none"/>
            <path d="M 70 155 Q 75 150 80 155" stroke="#D4AF37" strokeWidth="2.5" fill="none"/>
            <path d="M 85 155 Q 90 150 95 155" stroke="#D4AF37" strokeWidth="2.5" fill="none"/>
            
            <path d="M 55 175 Q 60 170 65 175" stroke="#D4AF37" strokeWidth="2.5" fill="none"/>
            <path d="M 70 175 Q 75 170 80 175" stroke="#D4AF37" strokeWidth="2.5" fill="none"/>
            <path d="M 85 175 Q 90 170 95 175" stroke="#D4AF37" strokeWidth="2.5" fill="none"/>
            
            {/* Horizontal decorative bands */}
            <line x1="47" y1="120" x2="113" y2="120" stroke="#D4AF37" strokeWidth="1.5"/>
            <line x1="47" y1="140" x2="113" y2="140" stroke="#D4AF37" strokeWidth="1.5"/>
            <line x1="47" y1="160" x2="113" y2="160" stroke="#D4AF37" strokeWidth="1.5"/>
            <line x1="47" y1="180" x2="113" y2="180" stroke="#D4AF37" strokeWidth="1.5"/>
            
            {/* Middle tier with lantern room */}
            <rect x="52" y="70" width="56" height="30" fill="#D4AF37" opacity="0.3"/>
            <rect x="54" y="72" width="52" height="26" fill="none" stroke="#D4AF37" strokeWidth="2"/>
            <path d="M 60 85 Q 67 80 74 85" stroke="#D4AF37" strokeWidth="2" fill="none"/>
            <path d="M 76 85 Q 83 80 90 85" stroke="#D4AF37" strokeWidth="2" fill="none"/>
            
            {/* Top tier */}
            <rect x="60" y="45" width="40" height="25" fill="#D4AF37" opacity="0.3"/>
            <rect x="62" y="47" width="36" height="21" fill="none" stroke="#D4AF37" strokeWidth="2"/>
            <path d="M 68 58 Q 80 54 92 58" stroke="#D4AF37" strokeWidth="1.5" fill="none"/>
            
            {/* Dome and finial */}
            <ellipse cx="80" cy="32" rx="18" ry="13" fill="#D4AF37" opacity="0.4" stroke="#D4AF37" strokeWidth="2"/>
            <rect x="78" y="18" width="4" height="14" fill="#D4AF37"/>
            <circle cx="80" cy="16" r="5" fill="none" stroke="#D4AF37" strokeWidth="2"/>
            <line x1="80" y1="11" x2="80" y2="5" stroke="#D4AF37" strokeWidth="2"/>
          </svg>

          {/* Decorative elements */}
          <div style={{ position: 'absolute', top: '-30px', right: '-30px', width: '120px', height: '120px', borderRadius: '50%', border: '1px solid rgba(212,175,55,0.08)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', bottom: '-20px', left: '-20px', width: '80px', height: '80px', borderRadius: '50%', border: '1px solid rgba(212,175,55,0.06)', pointerEvents: 'none' }} />

          {/* Balance header row */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', position: 'relative', zIndex: 1 }}>
            <p style={{ color: '#8B93A7', fontSize: '10px', margin: 0, textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '600' }}>
              {tr.walletBalance}
            </p>
            {/* Eye button - simple icon only, no background */}
            <motion.button
              data-testid="toggle-balance"
              whileTap={{ scale: 0.9 }}
              onClick={() => setBalanceVisible(!balanceVisible)}
              style={{
                background: 'none',
                border: 'none',
                padding: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#8B93A7',
              }}
            >
              {balanceVisible ? <EyeOpen /> : <EyeOff />}
            </motion.button>
          </div>

          {/* Balance amount */}
          <div
            style={{
              position: 'relative',
              zIndex: 1,
              minHeight: '43px',
              height: '43px',
              display: 'flex',
              alignItems: 'center',
              margin: '0 0 4px',
            }}
          >
            <motion.p
              animate={{ opacity: 1 }}
              style={{
                color: '#D4AF37',
                fontSize: '36px',
                fontWeight: '800',
                margin: 0,
                letterSpacing: '-1.8px',
                lineHeight: 1,
                userSelect: balanceVisible ? 'auto' : 'none',
                display: 'flex',
                alignItems: 'baseline',
              }}
            >
              {balanceVisible ? (
                <>
                  {balanceInt}
                  <span style={{ fontSize: '16px', fontWeight: '500', color: '#8B93A7', marginLeft: '5px' }}>{balanceDecimal} MAD</span>
                </>
              ) : (
                <>
                  <span style={{ fontSize: '36px', letterSpacing: '10px' }}>***</span>
                  <span style={{ fontSize: '16px', fontWeight: '500', color: 'transparent', marginLeft: '5px', visibility: 'hidden' }}>.00 MAD</span>
                </>
              )}
            </motion.p>
          </div>

          {/* SimoPay ID */}
          <p style={{ color: '#8B93A7', fontSize: '11px', fontWeight: '500', margin: '0 0 16px', letterSpacing: '0.2px', position: 'relative', zIndex: 1 }}>
            ahmed@simopay
          </p>

          <div style={{ display: 'inline-flex', background: 'rgba(212,175,55,0.1)', border: '1px solid rgba(212,175,55,0.18)', borderRadius: '100px', padding: '4px 11px', position: 'relative', zIndex: 1 }}>
            <span style={{ color: '#D4AF37', fontSize: '10px', fontWeight: '600' }}>{tr.thisMonthBadge}</span>
          </div>
        </div>
      </motion.div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.08 }}
        style={{ padding: '0 20px 20px' }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '8px' }}>
          {[
            { label: tr.send, action: 'send' },
            { label: tr.receive, action: 'receive' },
            { label: tr.topUp, action: 'topup' },
            { label: tr.scanQR, action: 'qrscan' },
          ].map((a) => (
            <motion.button
              key={a.action}
              data-testid={`quick-action-${a.action}`}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.91 }}
              onClick={() => onNavigate(a.action)}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '7px', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            >
              <div
                style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '17px',
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  display: 'grid',
                  placeItems: 'center',
                  lineHeight: 0,
                }}
              >
                {a.action === 'qrscan' ? (
                  <ScanQrIcon size={22} style={{ color: '#D4AF37' }} />
                ) : a.action === 'send' ? (
                  <WalletQuickSendIcon size={22} />
                ) : a.action === 'receive' ? (
                  <WalletQuickReceiveIcon size={22} />
                ) : (
                  <WalletQuickPlusIcon size={22} />
                )}
              </div>
              <span style={{ color: '#8B93A7', fontSize: '10px', fontWeight: '500', textAlign: 'center', lineHeight: 1.2 }}>{a.label}</span>
            </motion.button>
          ))}
        </div>
      </motion.div>

      {/* Recent Transactions */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.15 }}
        style={{ padding: '0 20px' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
          <h3 style={{ color: '#F5F1EA', fontSize: '15px', fontWeight: '700', margin: 0, letterSpacing: '-0.3px' }}>{tr.recent}</h3>
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => onNavigate('activity')}
            style={{ color: '#D4AF37', fontSize: '12px', fontWeight: '600', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            {tr.seeAll}
          </motion.button>
        </div>

        {recentTransactions.map((tx, i) => (
          <motion.div
            key={tx.id}
            initial={{ opacity: 0, x: isRTL ? 10 : -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.35, delay: 0.1 + i * 0.05 }}
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '11px 0', borderBottom: i < recentTransactions.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
              <div style={{ width: '38px', height: '38px', borderRadius: '13px', background: tx.positive ? 'rgba(74,222,128,0.07)' : 'rgba(255,255,255,0.05)', border: tx.positive ? '1px solid rgba(74,222,128,0.1)' : '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', flexShrink: 0 }}>{tx.icon}</div>
              <div>
                <p style={{ color: '#F5F1EA', fontSize: '13px', margin: 0, fontWeight: '600', letterSpacing: '-0.2px' }}>{txDisplayName(tx, t)}</p>
                <p style={{ color: '#8B93A7', fontSize: '10px', margin: 0 }}>{txTimeLabel(tx, t)}</p>
              </div>
            </div>
            <span style={{ color: tx.positive ? '#4ADE80' : '#F5F1EA', fontSize: '13px', fontWeight: '700', letterSpacing: '-0.3px', flexShrink: 0 }}>
              {tx.amount} MAD
            </span>
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
};

export default HomeScreen;
